import React, {useRef, useState} from 'react';
import {ScrollView, StyleProp, StyleSheet, TextStyle, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import AddToWallet from '../../components/rewards/AddToWallet';
import MemberCard from '../../components/rewards/MemberCard';
import SafeAreaPadding from '../../components/SafeAreaPadding';
import Button from '../../components/themed/Button';
import Text from '../../components/themed/Text';
import ContentView from '../../components/ui-controls/layout/ContentView';
import * as designTokens from '../../design-system/tokens';
import {useForcedSignOut} from '../../hooks/useForcedSignOut';
import logoImg from '../../images/brand/header-logo.png';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {resolveMemberDisplayName, rewardsMarketingName} from '../../utils/rewards';
import SignUpPageLayout, {
  CTA_HEIGHT,
  CTA_RADIUS,
  useWideLayout,
} from '../account/signUp/SignUpPageLayout';

import {prepareSignInThenHome, resetToMainScreen, resetToRoute} from './resetToHome';

const RewardsSuccess = observer(
  ({
    route,
  }: RouteNavigatorScreenProps<AppRoute.REWARDS_SUCCESS | AppRoute.REWARDS_ACTIVATE_SUCCESS>) => {
    const {rewardsStore, userStore, shopSelectionStore} = useStores();
    const {isDesktopCard: isWide, onLayout} = useWideLayout();

    // DIS-1826: the route name, not a param, says which journey ended here.
    const isActivate = route.name === AppRoute.REWARDS_ACTIVATE_SUCCESS;

    // DIS-1826 (spec D1): the sign-out below clears every store value this screen
    // renders, so the first render captures them once and the JSX reads only this.
    const [snapshot] = useState(() => ({
      cardNumber: rewardsStore.cardNumber || '',
      firstName: userStore.firstName || rewardsStore.firstName || '',
      // DIS-1245: prefer rewardsStore (API/form name from lite signup) over
      // userStore (only populated for full-session users). Subsumes the
      // DIS-1187 defensive composition — the helper also handles the
      // userStore.name `" "` truthy-empty quirk that broke the old fallback.
      memberName: resolveMemberDisplayName(
        {firstName: rewardsStore.firstName, lastName: rewardsStore.lastName},
        {firstName: userStore.firstName, lastName: userStore.lastName},
        'Success',
      ),
      walletUrl: rewardsStore.walletUrl || '',
      memberSince: rewardsStore.memberSince,
      isStaffSignup: rewardsStore.isStaffSignup,
      isLmgStaff: rewardsStore.isLmgStaff,
      hasSelectedStore: !!shopSelectionStore?.selectedStore?.id,
      // DIS-2006: REWARDS is registered only for liquor stores; a gift-card store falls back
      // to the gift-card home, so it goes to ACCOUNT_REWARDS (registered for all stores).
      isGiftCardStore: !!shopSelectionStore?.isSelectedStoreGiftCardStore,
      // DIS-1859: prefills Find Your Store when no store is selected. Read
      // before the forced sign-out clears it.
      suburb: rewardsStore.suburb?.locality ?? '',
      // DIS-1826: true only when signUp / signUpStaff just succeeded in memory.
      liteSignUpJustCompleted: rewardsStore.liteSignUpJustCompleted,
    }));

    // DIS-1826 (spec D1, D6): a lite sign-up ends signed out; opt-in stays signed in.
    // A deep link or reload of this route has no marker, so it does not sign out.
    const {whenSignedOut} = useForcedSignOut(!isActivate && snapshot.liteSignUpJustCompleted);

    const [isLeaving, setIsLeaving] = useState(false);
    const leavingRef = useRef(false);

    // DIS-1859: sign-in, then Home. No store: Find Your Store first.
    const handleSignInPress = async () => {
      if (leavingRef.current) {
        return;
      }
      leavingRef.current = true;
      setIsLeaving(true);
      await whenSignedOut();
      prepareSignInThenHome({userStore}, snapshot.hasSelectedStore, snapshot.suburb);
      resetToMainScreen(AppRoute.ACCOUNT_NOT_LOGGED_IN, snapshot.hasSelectedStore);
    };

    // DIS-1826 (spec D6): opt-in success goes straight to the member's Rewards page.
    // DIS-2006: that is the Rewards list (REWARDS), not the member card screen
    // (ACCOUNT_REWARDS). With no store, resetToMainScreen opens store selection
    // first, as it did for ACCOUNT_REWARDS.
    // DIS-1917: a pending offer return wins. Only with a store: without one the
    // offer screen's store gate could not have been passed, so the intent is left
    // to expire. A root reset, as below, so the rewards flow leaves the stack.
    const handleGoToRewardsPress = () => {
      const offerRoute = snapshot.hasSelectedStore ? userStore.takeOfferReturn() : null;
      if (offerRoute) {
        resetToRoute(offerRoute);
        return;
      }
      resetToMainScreen(
        snapshot.isGiftCardStore ? AppRoute.ACCOUNT_REWARDS : AppRoute.REWARDS,
        snapshot.hasSelectedStore,
      );
    };

    const headingCopy = `HERE’S YOUR ${rewardsMarketingName.toUpperCase()} CARD`;
    // A function, not a fragment, so the Text keeps the same children array on
    // both widths.
    const renderBody = (style: StyleProp<TextStyle>) => (
      <Text style={style}>
        {snapshot.firstName ? `Nice one, ${snapshot.firstName}!` : 'Welcome!'} You are now a part of
        our
        {` ${rewardsMarketingName} `}
        program! Show this barcode at checkout to collect awesome rewards!
      </Text>
    );
    const memberCard = (
      <MemberCard
        logoImg={logoImg}
        cardNumber={snapshot.cardNumber}
        name={snapshot.memberName}
        memberSince={snapshot.memberSince}
        isStaff={snapshot.isStaffSignup}
        isLmgStaff={snapshot.isLmgStaff}
      />
    );
    const emailHeadingCopy = 'Check your email';
    const emailBodyCopy =
      'Although your account is ready to go, we have sent you an email to complete the last few steps and give you access to our website to see all of your member benefits.';

    // DIS-1826 (spec D4, D6, D8): one CTA per route. Same outlined style on
    // both routes. DIS-1859: the sign-up label is START SHOPPING; the tap
    // still opens sign-in, which lands on Home.
    const cta = (
      <Button
        secondary
        title={isActivate ? 'GO TO REWARDS' : 'START SHOPPING'}
        onPress={isActivate ? handleGoToRewardsPress : handleSignInPress}
        disabled={isLeaving}
        loading={isLeaving}
        buttonStyle={localStyles.cta}
        titleStyle={designTokens.type.button.primary}
      />
    );

    // Desktop: the centred card (standard from 536:4247; S7 has no desktop
    // frame). Heading and body use the Welcome card's tokens (H1, P1, centred).
    // The member card and CTA sit in the 343 form column; "Check your email"
    // is a text block at the heading's width (SignUpPageLayout `outro`). No
    // AddToWallet on desktop: a wallet pass is a phone feature.
    if (isWide) {
      return (
        <ScrollView style={{backgroundColor: 'white'}}>
          <SignUpPageLayout
            centred
            isWide={isWide}
            onLayout={onLayout}
            testID="rewardsSuccessScreen"
            heading={
              <Text
                style={[
                  designTokens.type.heading.h1,
                  designTokens.lineHeight.heading.h1,
                  localStyles.headingWide,
                  localStyles.centred,
                ]}>
                {headingCopy}
              </Text>
            }
            intro={renderBody([
              designTokens.type.paragraph.p1,
              designTokens.lineHeight.paragraph.p1,
              localStyles.centred,
            ])}
            main={memberCard}
            outro={
              isActivate ? undefined : (
                <>
                  <Text style={[designTokens.type.heading.h2, localStyles.centred]}>
                    {emailHeadingCopy}
                  </Text>
                  <Text
                    style={[
                      designTokens.type.paragraph.p2,
                      designTokens.lineHeight.paragraph.p2,
                      localStyles.centred,
                      localStyles.emailBodyWide,
                    ]}>
                    {emailBodyCopy}
                  </Text>
                </>
              )
            }
            aside={cta}
          />
          <SafeAreaPadding bottom />
        </ScrollView>
      );
    }

    return (
      <ScrollView style={{backgroundColor: 'white'}}>
        <ContentView onLayout={onLayout}>
          <Text style={[designTokens.type.heading.h1, localStyles.heading]}>{headingCopy}</Text>

          {renderBody(designTokens.type.paragraph.p2)}
          <View style={localStyles.card}>
            {memberCard}
            {/* DIS-1556: Zen only issues the consumer card template, so the wallet
                pass never generates for staff members. Hide the dead button. */}
            {!snapshot.isStaffSignup && !snapshot.isLmgStaff && (
              <AddToWallet walletUrl={snapshot.walletUrl} />
            )}
          </View>

          {!isActivate && (
            <>
              <Text style={[designTokens.type.heading.h2, localStyles.sectionHeading]}>
                {emailHeadingCopy}
              </Text>
              <Text style={[designTokens.type.paragraph.p2, localStyles.emailBody]}>
                {emailBodyCopy}
              </Text>
            </>
          )}

          <View style={localStyles.ctaRow}>{cta}</View>
          {/* DIS-1628: the rewards flow is a ROOT sibling of DrawerNavigator, so no
              ancestor applies the bottom inset — without this the CTA sits behind the
              Android system nav bar and the iOS home indicator. */}
          <SafeAreaPadding bottom />
        </ContentView>
      </ScrollView>
    );
  },
);

export default RewardsSuccess;

// SHORTCUT: the spacing literals left in localStyles. The values keep this screen's
// pre-DIS-1730 spacing (heading1 variant margins, card gap, section margins).
// Ceiling: this screen. Upgrade path: swap the remaining literals for space
// tokens in a later DIS-1735 pass. The
// colours are theme-resolved, not literals.
const localStyles = StyleSheet.create({
  heading: {
    color: theme.colour.text.heading.primary,
    marginVertical: 20,
  },
  // Wide only: the centred card spaces the heading itself.
  headingWide: {
    color: theme.colour.text.heading.primary,
  },
  centred: {
    textAlign: 'center',
  },
  // Desktop only: email heading -> body is a related gap (layout.desktop).
  // SignUpPageLayout's outro owns member card -> heading and body -> CTA.
  emailBodyWide: {
    marginTop: designTokens.layout.desktop.stackRelated,
  },
  card: {
    gap: 8,
    marginTop: 24,
  },
  sectionHeading: {
    marginTop: designTokens.space.s40,
  },
  emailBody: {
    marginTop: designTokens.space.s16,
  },
  ctaRow: {
    marginTop: designTokens.space.s40,
  },
  // Outlined variant: the themed `secondary` button supplies the white fill and
  // the brand border. Height and radius come from SignUpPageLayout so this CTA
  // matches the other sign-up CTAs.
  cta: {
    width: '100%',
    height: CTA_HEIGHT,
    borderRadius: CTA_RADIUS,
  },
});
